'use client';

import { CreditCard, Blocks } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLocale } from '@/features/marketing/hooks/useLocale';
import type { BilingualContent } from '@/features/marketing/hooks/useLocale';
import { PAPER_BAND, PAPER_INK, PAPER_RULE } from '@/features/marketing/ui/paper-tokens';

/**
 * MarketingDisclaimer — R3 judge-E-verdict.md Decision 9. A reusable, neutral
 * disclaimer bubble with a FIXED contract: two icons, two placements, one type
 * recipe. No arbitrary color/radius/className props, no motion, no timer, no
 * hover, no tooltip — every caller renders the identical inner surface.
 *
 * Placements:
 *  - 'seam': absolutely centered over a section boundary. The boundary's own
 *    1px #FFFFFF12 hairline is drawn by the CALLER (F-lane's interleaved
 *    DisclaimerDivider slot in LandingPage — r3-debate-rulings.md Harm. b),
 *    never by this component; drawing a second line here would double the
 *    alpha read at the seam (verdict §5 Risk 6 "One hairline"). The parent
 *    that renders this in 'seam' mode MUST be `position:relative` — this
 *    component supplies only `position:absolute; left:50%;
 *    translate(-50%, 50%)`, it does not create its own positioning context.
 *  - 'floating': the same inner recipe, normal document flow, no transform —
 *    for callers that need the bubble as an ordinary block (e.g. Judge D's
 *    bot terms, per the verdict's cross-lane note).
 */
export type MarketingDisclaimerCopy = {
  lead: BilingualContent;
  detail?: BilingualContent;
};

export type MarketingDisclaimerProps = {
  copy: MarketingDisclaimerCopy;
  icon: 'card' | 'module';
  placement: 'seam' | 'floating';
};

const DISCLAIMER_ICONS = { card: CreditCard, module: Blocks } as const;

// R7 paper flip (hallmark editorial reskin, 2026-07-17): surface converts
// from the dark Surface-style card (fill+inset-wash+heavy shadow) to a flat
// paper chip — PAPER_BAND.base fill (lightest paper step, reads as "sitting
// on top of" whichever paper band the caller's section uses) + a single
// PAPER_RULE.hairline border, no shadow (editorial genre bans glassmorphism/
// elevation FX; a flat hairline-ruled surface is the house paper-card
// recipe — same discipline PricingSection.tsx's TierCard now follows).
// Desktop reads as one horizontal line (min-height 44px); mobile stacks lead
// then detail as two left-aligned lines (min-height 68px).
const BUBBLE_CLASSES = cn(
  'flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-2.5',
  'border',
  PAPER_RULE.hairline.border,
  PAPER_BAND.base.bg,
  'px-[14px] py-[12px] sm:px-[18px] sm:py-[10px]',
  'rounded-2xl sm:rounded-full',
  'min-h-[68px] sm:min-h-[44px]',
  'w-[calc(100vw-32px)] sm:w-[min(720px,calc(100vw-48px))]',
  // max-w-full (max-width:100%) resolves against the CALLER's containing
  // block. 'seam' placement's immediate ancestor is shrink-to-fit
  // (width:auto), so the percentage has no definite base and is a no-op
  // there — the viewport-relative width above governs unchanged. 'floating'
  // placement's ancestor (e.g. a fixed 420/480px grid track) HAS a definite
  // width, so this actually clamps the bubble to it — fixing the bot-section
  // right-track overflow without a second width recipe or a variant prop.
  'max-w-full'
);

export default function MarketingDisclaimer({ copy, icon, placement }: MarketingDisclaimerProps) {
  const { t } = useLocale();
  const Icon = DISCLAIMER_ICONS[icon];

  const inner = (
    <div role="note" className={BUBBLE_CLASSES}>
      <div className="flex items-center gap-2.5 sm:contents">
        <Icon className={cn('w-4 h-4 flex-shrink-0', PAPER_INK.muted.text)} aria-hidden="true" />
        <span className={cn('text-sm leading-5 font-semibold', PAPER_INK.primary.text)}>
          {t(copy.lead)}
        </span>
      </div>
      {copy.detail && (
        <span className={cn('text-sm leading-5', PAPER_INK.secondary.text)}>{t(copy.detail)}</span>
      )}
    </div>
  );

  if (placement === 'floating') {
    return inner;
  }

  return (
    <div className="absolute left-1/2 -translate-x-1/2 translate-y-1/2 flex justify-center">
      {inner}
    </div>
  );
}
